import type { ReactNode } from 'react'
import type { DocumentStatus, Quantity } from '@project/contracts'
import { formatQty } from '@project/contracts'
import { Money } from './Money.js'
import { StatusMark } from './StatusMark.js'

export type CellType = 'text' | 'docno' | 'date' | 'money' | 'qty' | 'status' | 'bool'

export interface ColumnDef<T> {
  key: string
  header: string
  type?: CellType
  align?: 'left' | 'right'
  /** CSS width, e.g. 160 or '18%'. Omit to let the column find its own. */
  width?: number | string
  render?: (row: T) => ReactNode
}

export interface TableProps<T> {
  columns: ReadonlyArray<ColumnDef<T>>
  rows: ReadonlyArray<T>
  getKey: (row: T) => string
  onRowClick?: (row: T) => void
  /** Shown in place of rows when there are none. */
  empty?: ReactNode
  /**
   * Fold each row into a labelled card on a narrow screen instead of leaving
   * it to scroll sideways.
   *
   * For the tables somebody TYPES into. A twelve-column line grid on a phone
   * is a sideways drag between the material and the quantity you are entering
   * for it, which is the one pairing that has to be visible at once — entering
   * a material request on site is the most-used mobile action in this business
   * (docs/video-notes/08). A read-only list is better left scrolling: the
   * columns are how it is scanned.
   */
  stacked?: boolean
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** dd-MMM-yyyy, the format every printed document in this system uses. */
export const formatDocDate = (value: string): string => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!iso) return value
  const [, y = '', m = '', d = ''] = iso
  const month = MONTHS[Number(m) - 1]
  return month ? `${d}-${month}-${y}` : value
}

const STATUSES = new Set(['DRAFT', 'IN_REVIEW', 'APPROVED', 'CANCELLED'])

const rightByDefault = (type: CellType | undefined): boolean =>
  type === 'money' || type === 'qty'

const renderCell = <T,>(column: ColumnDef<T>, row: T): ReactNode => {
  if (column.render) return column.render(row)

  const raw = (row as Record<string, unknown>)[column.key]
  if (raw === null || raw === undefined || raw === '') return null
  const text = String(raw)

  switch (column.type) {
    case 'money':
      return <Money value={text} />
    case 'qty':
      return <span className="num">{formatQty(text as Quantity)}</span>
    case 'docno':
      return <span className="mono">{text}</span>
    case 'date':
      return formatDocDate(text)
    case 'status':
      return STATUSES.has(text) ? <StatusMark status={text as DocumentStatus} /> : text
    // A boolean column reads as a word, not as `true`: a list of tick marks is
    // unreadable at fifty rows and unexportable to a spreadsheet.
    case 'bool':
      return text === 'true' || text === 'YES' || text === 'Yes' ? 'Yes' : 'No'
    default:
      return text
  }
}

export const Table = <T,>({ columns, rows, getKey, onRowClick, empty, stacked }: TableProps<T>) => (
  <div className={stacked ? 'tbl-wrap tbl-wrap--stacked' : 'tbl-wrap'}>
    <table className="tbl">
      <thead>
        <tr>
          {columns.map((c) => {
            const right = c.align ? c.align === 'right' : rightByDefault(c.type)
            return (
              <th
                key={c.key}
                scope="col"
                className={right ? 'is-right' : undefined}
                // A column with no width of its own is the one carrying the
                // words — a name, a description. It keeps enough room to read
                // in a line or two; past that the table scrolls in its card
                // rather than folding the name into a column of single words.
                style={c.width === undefined ? { minWidth: 200 } : { width: c.width }}
              >
                {c.header}
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="tbl__none" colSpan={Math.max(columns.length, 1)}>
              {empty ?? 'Nothing to show for this view.'}
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr
              key={getKey(row)}
              className={onRowClick ? 'tbl__row--click' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onRowClick(row)
                      }
                    }
                  : undefined
              }
            >
              {columns.map((c) => {
                const right = c.align ? c.align === 'right' : rightByDefault(c.type)
                return (
                  <td key={c.key} className={right ? 'is-right' : undefined} data-label={c.header}>
                    {renderCell(c, row)}
                  </td>
                )
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
)
